// src/CitiesPage.js
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ===================== ✅ City helpers (moved from App.js) ===================== */
export function normalizeSlug(s) {
  const str = String(s || "").trim().toLowerCase();
  if (!str) return "";

  const noDia = str.normalize
    ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    : str;

  return noDia
    .replace(/['’]/g, "")
    .replace(/[\s/_|]+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function bestCityName(p, locationText) {
  if (!p) return "";

  const cand =
    p.city ||
    p.town ||
    p.village ||
    p.municipality ||
    p.region ||
    p.location?.city ||
    p.location?.town ||
    p.location?.village ||
    p.location?.region;

  if (cand && String(cand).trim()) return String(cand).trim();

  const loc = typeof locationText === "function" ? locationText(p) : "";
  if (!loc) return "";
  const first = String(loc).split(",")[0];
  return String(first || "").trim();
}

export function titleCaseWords(s) {
  const clean = String(s || "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return "";
  return clean
    .split(" ")
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/* ===================== Shared Favorites (copied from App.js) ===================== */
function useFavsStorage() {
  const [favs, setFavs] = useState(() => {
    try {
      const raw = localStorage.getItem("favs");
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("favs", JSON.stringify(Array.from(favs)));
    } catch {}
  }, [favs]);

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return { favs, toggleFav };
}

/* ===================== Cities Directory (/cities) ===================== */
export default function CitiesPage({ properties }) {
  const { t } = useTranslation();

  const cities = useMemo(() => {
    const map = new Map();
    for (const p of properties || []) {
      const name = bestCityName(p, (x) => x?.location?.text || x?.location || "");
      const slug = normalizeSlug(name);
      if (!name || !slug) continue;

      const prev = map.get(slug);
      if (prev) map.set(slug, { name: prev.name, count: prev.count + 1 });
      else map.set(slug, { name, count: 1 });
    }

    const arr = Array.from(map.entries()).map(([slug, v]) => ({
      slug,
      name: titleCaseWords(v.name),
      count: v.count,
    }));

    arr.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    return arr;
  }, [properties]);

  useEffect(() => {
    document.title = `Cities – hellastay`;
  }, []);

  return (
    <div className="page" style={{ paddingTop: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, letterSpacing: -0.2 }}>
            {t("cities.h1", { defaultValue: "Cities" })}
          </h1>
          <div style={{ marginTop: 6, color: "rgba(0,0,0,0.62)", fontWeight: 600 }}>
            {cities.length} {t("cities.count", { defaultValue: "locations" })}
          </div>
        </div>

        <Link to="/" className="select-pill" style={{ textDecoration: "none" }}>
          ← {t("cities.back", { defaultValue: "Back to all stays" })}
        </Link>
      </div>

      <div style={{ marginTop: 18 }}>
        {cities.length === 0 ? (
          <div className="empty-card">
            <div className="empty-title">{t("cities.empty.title", { defaultValue: "No cities yet" })}</div>
            <div className="empty-sub">{t("cities.empty.sub", { defaultValue: "Add your first property to populate this page." })}</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/city/${encodeURIComponent(c.slug)}`}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  border: "1px solid rgba(0,0,0,0.10)",
                  borderRadius: 16,
                  background: "#fff",
                  padding: 14,
                  boxShadow: "0 10px 26px rgba(0,0,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <div style={{ fontWeight: 900 }}>{c.name}</div>
                <div style={{ opacity: 0.65, fontWeight: 900 }}>{c.count}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===================== City Page (/city/:slug) ===================== */
export function CityPage({ properties, locationText, safeId, PropertyCard }) {
  const { slug } = useParams();
  const { t } = useTranslation();

  const citySlug = useMemo(() => normalizeSlug(decodeURIComponent(String(slug || ""))), [slug]);

  const cityName = useMemo(() => {
    const raw = String(slug || "");
    const decoded = decodeURIComponent(raw);
    const nice = titleCaseWords(decoded);
    return nice || t("city.titleFallback", { defaultValue: "Location" });
  }, [slug, t]);

  useEffect(() => {
    document.title = `${cityName} – hellastay`;
  }, [cityName]);

  const { favs, toggleFav } = useFavsStorage();

  const list = useMemo(() => {
    const items = Array.isArray(properties) ? properties : [];
    if (!citySlug) return items;

    return items.filter((p) => {
      const cn = bestCityName(p, locationText);
      const s1 = normalizeSlug(cn);
      if (s1 && s1 === citySlug) return true;

      const loc = locationText(p);
      const s2 = normalizeSlug(loc);
      if (
        s2 &&
        (s2 === citySlug ||
          s2.startsWith(citySlug + "-") ||
          s2.includes("-" + citySlug + "-") ||
          s2.endsWith("-" + citySlug))
      )
        return true;

      return false;
    });
  }, [properties, citySlug, locationText]);

  return (
    <div className="page" style={{ paddingTop: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, letterSpacing: -0.2 }}>
            {t("city.h1", { defaultValue: "Stays in" })} {cityName}
          </h1>
          <div style={{ marginTop: 6, color: "rgba(0,0,0,0.62)", fontWeight: 600 }}>
            {list.length} {t(list.length === 1 ? "home.resultOne" : "home.resultMany")}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link to="/cities" className="select-pill" style={{ textDecoration: "none" }}>
            {t("city.allCities", { defaultValue: "All cities" })}
          </Link>
          <Link to="/" className="select-pill" style={{ textDecoration: "none" }}>
            ← {t("city.back", { defaultValue: "Back to all" })}
          </Link>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="hs-grid hs-listings">
          {list.length === 0 ? (
            <div className="empty-card">
              <div className="empty-title">{t("city.empty.title", { defaultValue: "No stays yet" })}</div>
              <div className="empty-sub">{t("city.empty.sub", { defaultValue: "Try another location or check back soon." })}</div>
            </div>
          ) : (
            list.map((p, idx) => {
              const id = safeId(p, `property-${idx}`);
              return <PropertyCard key={id} p={p} fallbackId={id} isFav={favs.has(id)} onToggleFav={toggleFav} />;
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ===================== Search Page (/search/:q) ===================== */
export function SearchPage({ properties, locationText, startingPrice, safeId, PropertyCard }) {
  const { q } = useParams();
  const { t } = useTranslation();

  const raw = useMemo(() => decodeURIComponent(String(q || "")), [q]);
  const queryText = useMemo(() => titleCaseWords(raw), [raw]);
  const querySlug = useMemo(() => normalizeSlug(raw), [raw]);

  useEffect(() => {
    document.title = `${queryText || "Search"} – hellastay`;
  }, [queryText]);

  const { favs, toggleFav } = useFavsStorage();
  const [maxPrice, setMaxPrice] = useState("");

  const list = useMemo(() => {
    const items = Array.isArray(properties) ? properties : [];
    const max = maxPrice === "" ? null : Number(maxPrice);

    return items.filter((p) => {
      const loc = locationText(p).toLowerCase();
      const title = String(p?.title || "").toLowerCase();
      const city = bestCityName(p, locationText).toLowerCase();
      const price = startingPrice(p);

      const hit =
        !querySlug ||
        loc.includes(querySlug) ||
        title.includes(querySlug) ||
        city.includes(querySlug) ||
        normalizeSlug(loc).includes(querySlug) ||
        normalizeSlug(city).includes(querySlug);

      const okPrice = max === null || price <= max;
      return hit && okPrice;
    });
  }, [properties, querySlug, maxPrice, locationText, startingPrice]);

  return (
    <div className="page" style={{ paddingTop: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, letterSpacing: -0.2 }}>
            {t("search.h1", { defaultValue: "Search results for" })} “{queryText || raw || ""}”
          </h1>
          <div style={{ marginTop: 6, color: "rgba(0,0,0,0.62)", fontWeight: 600 }}>
            {list.length} {t(list.length === 1 ? "home.resultOne" : "home.resultMany")}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            className="select-pill"
            style={{ width: 160 }}
            placeholder={t("home.maxPricePlaceholder")}
            type="number"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            min={0}
          />
          <Link to="/cities" className="select-pill" style={{ textDecoration: "none" }}>
            {t("search.cities", { defaultValue: "All cities" })}
          </Link>
          <Link to="/" className="select-pill" style={{ textDecoration: "none" }}>
            ← {t("search.back", { defaultValue: "Back" })}
          </Link>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="hs-grid hs-listings">
          {list.length === 0 ? (
            <div className="empty-card">
              <div className="empty-title">{t("search.empty.title", { defaultValue: "No stays found" })}</div>
              <div className="empty-sub">{t("search.empty.sub", { defaultValue: "Try another search." })}</div>
            </div>
          ) : (
            list.map((p, idx) => {
              const id = safeId(p, `property-${idx}`);
              return <PropertyCard key={id} p={p} fallbackId={id} isFav={favs.has(id)} onToggleFav={toggleFav} />;
            })
          )}
        </div>
      </div>
    </div>
  );
}